/**
 * Phase 134D — StreamX Broadcast Coach Concierge
 * --------------------------------------------------------------------
 * Floating bottom-right chat bubble that appears on /livex/studio after
 * 90 seconds of idle for unactivated academy_owners. Premium, subtle, NOT
 * customer-support-clone. Scope: activation only (how to go live, perms,
 * setup, title, replays). Powered by GPT-5.2 via Emergent LLM Key.
 *
 * Lifecycle:
 *   1. Mount: do nothing yet (parent gates via showAfterIdleMs).
 *   2. After 90s idle: render bubble + send 'opened' telemetry.
 *   3. User clicks bubble → opens compact chat panel + creates session.
 *   4. User sends message → POST /coach/chat → display assistant reply.
 *   5. Dismiss → 'dismissed' event, localStorage flag (one per academy).
 *   6. If parent detects first stream → 'activated' event.
 *
 * The component does NOT crash on coach engine errors — it shows a soft
 * fallback ("I'll be right back — meanwhile, tap Create Stream...").
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import {
  MessageCircle, X, Send, Loader2, Minimize2, ChevronUp, Sparkles,
} from 'lucide-react';
import '@/styles/livex.css';

const FALLBACK_REPLY = (
  "I'll be right back — meanwhile, tap Create Stream above to start your broadcast. " +
  "If your camera prompt didn't appear, click the camera icon in your browser address bar."
);

export default function LivexCoachConcierge({ academyId, sport, streamsCount, titleFilled, devicePermissions }) {
  const storageKey = academyId ? `livex_coach_dismissed_${academyId}` : null;
  const [stage, setStage] = useState('hidden');  // hidden | bubble | chat | dismissed
  const [sessionId, setSessionId] = useState('');
  const [opener, setOpener] = useState('');
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef(null);
  const dismissedRef = useRef(false);

  // Bail-out conditions.
  useEffect(() => {
    if (!academyId) { setStage('dismissed'); return; }
    try {
      if (localStorage.getItem(storageKey) === '1') {
        dismissedRef.current = true;
        setStage('dismissed');
      }
    } catch { /* ignore */ }
  }, [academyId, storageKey]);

  // 90-second idle gate before showing the bubble. Reset on user interaction.
  useEffect(() => {
    if (stage !== 'hidden') return;
    let timer = null;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!dismissedRef.current) setStage('bubble');
      }, 90 * 1000);
    };
    arm();
    const handler = () => arm();
    window.addEventListener('mousemove', handler, { passive: true });
    window.addEventListener('keydown', handler, { passive: true });
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('mousemove', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [stage]);

  // Build a context snapshot to send with every request.
  const buildContext = () => ({
    academy_name: undefined,        // backend will resolve via session/user
    sport: sport || undefined,
    streams_count: Number.isInteger(streamsCount) ? streamsCount : undefined,
    title_filled: !!titleFilled,
    camera_permission_state: devicePermissions || undefined,
    device_type: typeof window !== 'undefined' && window.innerWidth < 640 ? 'mobile' : 'desktop',
  });

  // Open the chat panel + create a session.
  const openChat = async () => {
    if (stage === 'chat') return;
    setError('');
    setStage('chat');
    if (sessionId) return;  // already initialised
    try {
      const r = await api.post('/livex/coach/session', { context: buildContext() });
      setSessionId(r.data?.session_id || '');
      setOpener(r.data?.opener || '');
    } catch (e) {
      setError("Coach isn't reachable right now — try again in a moment.");
    }
  };

  // Dismiss the entire concierge for this academy (sticky via localStorage).
  const dismiss = async () => {
    dismissedRef.current = true;
    if (storageKey) {
      try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    }
    if (sessionId) {
      try { await api.post('/livex/coach/event', { session_id: sessionId, event: 'dismissed' }); }
      catch { /* ignore */ }
    }
    setStage('dismissed');
  };

  const minimise = async () => {
    setStage('bubble');
    if (sessionId) {
      try { await api.post('/livex/coach/event', { session_id: sessionId, event: 'minimised' }); }
      catch { /* ignore */ }
    }
  };

  // Send a chat message.
  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || sending || !sessionId) return;
    setMessages((m) => [...m, { role: 'user', text }]);
    setDraft('');
    setSending(true);
    setError('');
    try {
      const r = await api.post('/livex/coach/chat', {
        session_id: sessionId,
        message: text,
        context: buildContext(),
      });
      const reply = r.data?.reply || FALLBACK_REPLY;
      setMessages((m) => [...m, { role: 'assistant', text: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: FALLBACK_REPLY }]);
      setError("Coach is offline. Showing a safe fallback.");
    } finally {
      setSending(false);
    }
  };

  // Auto-scroll on new messages.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, opener]);

  const quickPrompts = useMemo(() => [
    'How do I go live?',
    'My camera permission is blocked — what now?',
    'What should my match title look like?',
  ], []);

  if (stage === 'hidden' || stage === 'dismissed') return null;

  // ----- BUBBLE -----
  if (stage === 'bubble') {
    return (
      <button
        onClick={openChat}
        data-testid="livex-coach-bubble"
        aria-label="Open StreamX Broadcast Coach"
        className="fixed bottom-5 right-5 z-[120] inline-flex items-center gap-2 pl-3 pr-4 py-3 text-sm font-bold text-white rounded-2xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 livex-burst"
        style={{
          background: 'linear-gradient(135deg, #dc2626, #f97316)',
          boxShadow: '0 12px 32px rgba(255,90,31,0.40)',
        }}
      >
        <div className="grid place-items-center w-7 h-7 rounded-xl bg-white/15 backdrop-blur">
          <MessageCircle className="h-4 w-4 text-white" />
        </div>
        <div className="text-left">
          <div className="text-[10px] font-black uppercase tracking-[0.15em] leading-none text-white/80">STREAMX COACH</div>
          <div className="text-[13px] font-bold leading-tight">Need help going live?</div>
        </div>
      </button>
    );
  }

  // ----- CHAT PANEL -----
  return (
    <div
      data-testid="livex-coach-panel"
      role="dialog"
      aria-labelledby="livex-coach-panel-title"
      className="fixed bottom-5 right-5 z-[120] w-[min(380px,calc(100vw-2.5rem))] flex flex-col rounded-2xl border-2 overflow-hidden livex-burst"
      style={{
        background: '#07111F',
        borderColor: '#FF5A1F',
        boxShadow: '0 24px 64px rgba(0,0,0,0.50), 0 12px 32px rgba(255,90,31,0.20)',
        maxHeight: 'min(560px, calc(100vh - 3rem))',
      }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b-2 border-[#22314F]" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
        <div className="grid place-items-center w-8 h-8 rounded-xl bg-white/15 backdrop-blur flex-shrink-0">
          <Sparkles className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div id="livex-coach-panel-title" className="text-sm font-black text-white truncate">StreamX Broadcast Coach</div>
          <div className="text-[10px] font-bold text-white/80 uppercase tracking-[0.12em] leading-none">activation guide · gpt-5.2</div>
        </div>
        <button
          onClick={minimise}
          aria-label="Minimise coach"
          data-testid="livex-coach-minimise"
          className="inline-flex items-center justify-center w-8 h-8 text-white/85 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
        >
          <Minimize2 className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={dismiss}
          aria-label="Close coach"
          data-testid="livex-coach-close"
          className="inline-flex items-center justify-center w-8 h-8 text-white/85 hover:text-white hover:bg-white/15 rounded-lg transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        data-testid="livex-coach-messages"
        className="flex-1 overflow-y-auto px-3 py-4 space-y-3"
        style={{ background: 'linear-gradient(180deg, #050a14 0%, #07111F 100%)' }}
      >
        {opener && (
          <div className="flex">
            <div className="max-w-[85%] px-3 py-2.5 bg-[#0c1a2e] border border-[#22314F] rounded-2xl rounded-bl-md text-sm text-white leading-relaxed">
              {opener}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`livex-coach-msg-${m.role}`}>
            <div
              className={`max-w-[85%] px-3 py-2.5 text-sm leading-relaxed ${
                m.role === 'user'
                  ? 'bg-[#FF5A1F] text-white rounded-2xl rounded-br-md font-medium'
                  : 'bg-[#0c1a2e] border border-[#22314F] text-white rounded-2xl rounded-bl-md whitespace-pre-line'
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex" data-testid="livex-coach-typing">
            <div className="max-w-[85%] px-3 py-2.5 bg-[#0c1a2e] border border-[#22314F] rounded-2xl rounded-bl-md text-sm text-white inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-[#FF5A1F]" />
              Coach is typing…
            </div>
          </div>
        )}
        {error && (
          <div className="text-[11px] font-bold text-rose-300 text-center" data-testid="livex-coach-error">{error}</div>
        )}
      </div>

      {/* Quick prompts (only show until user has sent first message) */}
      {messages.length === 0 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="livex-coach-quick-prompts">
          {quickPrompts.map((p) => (
            <button
              key={p}
              onClick={() => { setDraft(p); }}
              data-testid={`livex-coach-quick-${p.split(' ')[0].toLowerCase()}`}
              className="px-2.5 py-1 text-[11px] font-bold text-[#fed7aa] bg-[#1a0e10] border border-[#3a1a1f] hover:border-[#FF5A1F] rounded-full transition-all"
            >
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      <form onSubmit={sendMessage} className="flex items-center gap-2 px-3 py-3 border-t-2 border-[#22314F] bg-[#050a14]">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask anything about going live…"
          aria-label="Message StreamX Coach"
          data-testid="livex-coach-input"
          disabled={sending || !sessionId}
          maxLength={1000}
          className="flex-1 px-3 py-2 text-sm text-white bg-[#07111F] border-2 border-[#22314F] focus:border-[#FF5A1F] outline-none rounded-lg disabled:opacity-50"
          style={{ fontSize: '14px' }}
        />
        <button
          type="submit"
          disabled={!draft.trim() || sending || !sessionId}
          data-testid="livex-coach-send"
          aria-label="Send message"
          className="inline-flex items-center justify-center w-10 h-10 text-white rounded-lg border-2 border-[#FF5A1F] disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
